import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import { theme, VStack } from '@sg/garnish';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { type PartialLineItem, type PartialProduct } from '../../../../graphql';
import { useNavigateToCustomizationEditScreen } from '../../hooks';
import { BagLineItem } from './BagLineItem';

/**
 * This component is a list of {BagLineItem}.
 * It also binds the navigation callback for when a bag line item is pressed.
 */
export const BagLineItems = (props: BagLineItemsProps) => {
  const {
    lineItems = [],
    lineItemIdsBeingUpdated,
    restaurantSlug,
    addressId,
    isDelivery,
    isDisabled,
    trackLineItemPressed,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
  } = props;

  const { track } = useTelemetry();

  const { navigateToCustomizationEditScreen } = useNavigateToCustomizationEditScreen({
    restaurantSlug,
    addressId,
    isDelivery,
  });

  const handleOnPress = useCallback(
    (lineItemId: string, product: PartialProduct) => {
      trackLineItemPressed();
      track('pdp.view', {
        entryPoint: 'bag',
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: [restaurantSlug, addressId].join(' | '),
        }),
      });

      navigateToCustomizationEditScreen({
        lineItemId,
        product,
      });
    },
    [restaurantSlug, addressId, track, trackLineItemPressed, navigateToCustomizationEditScreen],
  );

  return (
    <VStack
      hasDivider
      dividerColor={theme.colors.LIGHT}
      gap={theme.spacing['4']}
      style={styles.container}
    >
      {lineItems.map((lineItem) => {
        const isUpdatingAnyItem = lineItemIdsBeingUpdated.length > 0;
        const isUpdatingThisItem = lineItemIdsBeingUpdated.includes(lineItem.id ?? '');
        const shouldBlockInteraction = isDisabled || (isUpdatingAnyItem && !isUpdatingThisItem);

        return (
          <View key={lineItem.id} style={styles.content}>
            <BagLineItem
              lineItem={lineItem}
              isDisabled={shouldBlockInteraction}
              startChangingLineItemQuantity={startChangingLineItemQuantity}
              stopChangingLineItemQuantity={stopChangingLineItemQuantity}
              changeLineItemQuantity={changeLineItemQuantity}
              onPress={handleOnPress}
            />
          </View>
        );
      })}
    </VStack>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
  },
  content: {
    paddingHorizontal: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagLineItemsProps = {
  lineItems?: readonly PartialLineItem[];
  lineItemIdsBeingUpdated: readonly string[];
  restaurantSlug?: string;
  addressId?: string;
  isDelivery?: boolean;
  isDisabled: boolean;
  trackLineItemPressed: () => void;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
  changeLineItemQuantity: (params: ChangeLineItemQuantityParams) => void;
};

type ChangeLineItemQuantityParams = {
  lineItemId: string;
  productId: string;
  quantity: number;
  resetQuantity?: () => void;
};
